import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { Box, Button, Checkbox, Grid, IconButton, Pagination, TableCell, Tooltip } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Card from '@mui/material/Card';
import Popper from '@mui/material/Popper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  HeaderGroup, Row, useAsyncDebounce, useGlobalFilter, usePagination, useSortBy, useTable
} from 'react-table';
import theme from '../../../../assets/theme';
import colors from "../../../../assets/theme/base/colors";
import { ColumnSortOption, SortDirections } from '../../../Model/Dto/ColumnSortOption';
import { SkeletonLoading } from '../../SkeletonLoading/SkeletonLoading';
import { filterGrey, tableHeaderColour } from '../BEE123.Constants';
import { MDBox } from '../MDBox';
import { MDButton } from '../MDButton';
import { MDInput } from '../MDInput';
import { MDTypography } from '../MDTypography';
import { IBulkAction } from './BulkAction';
import { DataTableMockData } from './Data/DataTableMockData';
import './DataTable.scss';
import { DataTableBodyCell } from './DataTableBodyCell';
import { DataTableHeadCell } from './DataTableHeadCell';
import { FilterType, IFilter } from './Filters';
import { isNullOrEmpty } from '../../../Model/helpers';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formControlColour, tooltipColour } from '../BEE123.Constants';
import { isNullOrUndefined } from 'util';
import { toast } from 'react-toastify';
import { ValidatedMobileDatePicker } from '../ValidatedMobileDatePicker/ValidatedMobileDatePicker';

const { dark } = colors;

interface IDataTableProps {
  entriesPerPage?: {
    defaultValue?: number,
    entries?: number[]
  };
  canSearch?: boolean;
  showTotalEntries?: boolean;
  showEntriesPerPage?: boolean
  showBottomMargin?: boolean;
  table?: any;
  pagination?: {
    variant?: 'contained' | 'gradient',
    color?: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error' | 'dark' | 'light'
  };
  isSorted?: boolean;
  noEndBorder?: boolean;
  totalItemsCount?: number;
  filteredItemsCount?: number;
  pageNumber?: number;
  bulkActions?: IBulkAction[];
  isButtonOutlined?: boolean;
  filters?: IFilter[];
  entriesPerPageChanged?: (size: number) => void;
  pageNumberChanged?: (page: number) => void;
  searchValueChanged?: (searchValue: string) => void;
  filtersChanged?: (filterProps: {}) => void;
  columnSortingChanged?: (columnSortOption: ColumnSortOption) => void;
  isLoading?: boolean;
  manualSorting?: boolean;
  searchBoxText?: string;
  showSearchBoxInfoIcon?: boolean;
  searchBoxInfoIconTooltipText?: string;
  showFilteredCount?: boolean;
  noDataMessage?: string;
}

export const DataTable: React.FC<IDataTableProps> = ({
  entriesPerPage = { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch = false,
  showTotalEntries = true,
  showEntriesPerPage = true,
  showBottomMargin = true,
  table = DataTableMockData,
  pagination = { variant: 'gradient', color: 'info' },
  isSorted = true,
  noEndBorder = false,
  totalItemsCount,
  filteredItemsCount,
  pageNumber = 0,
  isButtonOutlined = false,
  bulkActions = [],
  filters = [],
  entriesPerPageChanged = (size: number) => { },
  pageNumberChanged = (page: number) => { },
  searchValueChanged = (searchValue: string) => { },
  filtersChanged = (filterProps: {}) => { },
  columnSortingChanged = (columnSortOption: ColumnSortOption) => { },
  isLoading = false,
  manualSorting = false,
  searchBoxText = 'Search...',
  showSearchBoxInfoIcon = false,
  searchBoxInfoIconTooltipText = '',
  showFilteredCount = true,
  noDataMessage

}: IDataTableProps) => {
  const [redraw, setRedraw] = useState<number>(0);
  const [filterControlValues, setFilterControlValues] = useState<any>({});//selected values on filter controls
  const [filterProps, setFilterProps] = useState<{ [key: string]: any }>({});//return object with filter values selected
  const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 10;
  const entries = entriesPerPage.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ['5', '10', '15', '20', '25'];
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const [overflowActive, setOverflowActive] = useState(false);
  const [lastColumnIsActions, setLastColumnIsActions] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (tableContainerRef.current) {
        const tableContainer = tableContainerRef.current;
        const table = tableContainer.querySelector("table");

        if (table) {
          setOverflowActive(table.scrollWidth > tableContainer.clientWidth);
          const lastColumnHeader = table.querySelector("th:last-child");
          if (lastColumnHeader) {
            setLastColumnIsActions(lastColumnHeader.textContent === "Actions");
          }
        }
      }
    };

    const observer = new MutationObserver(checkOverflow);
    const observerConfig = {
      childList: true,
      subtree: true,
    };

    if (tableContainerRef.current) {
      observer.observe(tableContainerRef.current, observerConfig);
    }

    // Check for overflow after initial render
    setTimeout(checkOverflow, 0);

    const handleResize = () => {
      checkOverflow();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, [table, tableContainerRef]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0 } as any,
      manualSortBy: manualSorting
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    //pageOptions,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageSize, globalFilter, sortBy },
  } = tableInstance;

  // Set the default value for the entries per page when component mounts
  useEffect(() => {
    setPageSize(defaultValue || 10);
    if (manualSorting) {
      handleSort(sortBy);
    }
  }, [defaultValue, redraw, sortBy]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value: number) => {
    setPageSize(value);
  }

  const handleSort = (sortByVal: any) => {
    if (sortByVal.length > 0) {
      var columnSortValue: ColumnSortOption = {
        ColumnName: sortByVal[0].id,
        SortDirection: sortByVal[0].desc === false ? SortDirections.ASC : SortDirections.DESC
      }
      columnSortingChanged(columnSortValue);
    }
  }

  //Fix incorrect indexing
  if (pageNumber > 0) {
    pageNumber = pageNumber - 1;
  }

  if(filteredItemsCount === undefined){
    filteredItemsCount = 0
  }

  const pageIndex = pageNumber;
  // Create an array of page numbers based on count
  const pageCount = Math.ceil(Number(filteredItemsCount) / pageSize);
  const pageOptions = Array.from(Array(pageCount).keys()).map(x => x + 1);

  const renderFilters = () => {
    return (
      <>
        <Grid container spacing={1}>
          {filters && filters.map((filter: IFilter, index) => (
            <>
              <Grid item xs={4} key={`lbl-filter-${index}`}>
                <MDTypography variant="button" component='div'>{filter.label}</MDTypography>
              </Grid>
              <Grid item xs={8} key={`filter-ctrl-${index}`}>
                {renderFilterControl(filter)}
              </Grid>
            </>
          ))}
        </Grid>
        <Grid justifyContent="end" container mt={1} spacing={1}>
          <Grid item>
            <Button variant="contained" component="span" onClick={applyFilters}>
              Apply
            </Button>
          </Grid>
          <Grid item>
            <Button variant="contained" component="span" onClick={clearFilters}>
              Clear
            </Button>
          </Grid>
        </Grid>
      </>);
  }

  const getDatepickerValue = (controlName: string) => {
    return filterControlValues?.[controlName] ?? null;
  }

  const getDateRangeValue = (controlName: string, propName: string) => {
    let rangeVal = filterControlValues?.[controlName]?.[propName] ?? null;
    return rangeVal;
  }

  const getAgeRangeValue = (controlName: string, propName: string) => {
    let ageValue = filterControlValues?.[controlName]?.[propName] ?? null ;
    return ageValue;
  }

  const renderFilterControl = (filter: IFilter) => {
    let controlName: string;
    switch (filter.type) {
      case FilterType.select:
        controlName = `filter-select-${filter.propname}`;
        return (
          <Autocomplete
            id={controlName}
            key={controlName}
            options={filter.values ?? []} 
            value={filterControlValues[controlName]}
            size='small'
            getOptionLabel={(option) => option.key}
            onChange={(event, newValues) => {
              let fcValues = filterControlValues;
              fcValues[controlName] = newValues;
              setFilterControlValues(fcValues);
              singleValueFilterChanged(filter.propname, newValues);
            }}
            renderInput={(params) => (
              <TextField {...params} />
            )}
          />
        )

      case FilterType.dateRange:
        controlName = `filter-daterange-${filter.propname}`;

        return (
          <Grid container spacing={1}>
            <LocalizationProvider dateAdapter={AdapterDateFns} key={controlName}>
              <Grid item xs={6}>
                <ValidatedMobileDatePicker
                  inputFormat="yyyy/MM/dd"
                  mask="____/__/__"
                  label="Start"
                  value={getDateRangeValue(controlName, 'start')}
                  maxDate={getDateRangeValue(controlName, 'end')}
                  onChange={(newValue: any) => {
                    /*set daterange value ~~ fcValues[controlName] = {start: newValue, end: whatever}*/
                    let fcValues = { ...filterControlValues };
                    let fcValuesStartEnd = fcValues[controlName];
                    let newfcValuesStartEnd = { ...fcValuesStartEnd, start: newValue };
                    fcValues[controlName] = newfcValuesStartEnd;
                    setFilterControlValues(fcValues);
                    mulitpleValueFilterChanged(filter.propname, newfcValuesStartEnd);
                  }}
                  renderInput={(params: any) => <TextField {...params} size='small' />}
                  views={['year', 'month', 'day']}
                />
              </Grid>
              <Grid item xs={6}>
                <ValidatedMobileDatePicker
                  inputFormat="yyyy/MM/dd"
                  mask="____/__/__"
                  label="End"
                  value={getDateRangeValue(controlName, 'end')}
                  minDate={getDateRangeValue(controlName, 'start')}
                  onChange={(newValue: any) => {
                    /*set daterange value ~~ fcValues[controlName] = {start: whatever, end: newValue}*/
                    let fcValues = { ...filterControlValues };
                    let fcValuesStartEnd = fcValues[controlName];
                    let newfcValuesStartEnd = { ...fcValuesStartEnd, end: newValue };
                    fcValues[controlName] = newfcValuesStartEnd;
                    setFilterControlValues(fcValues);
                    mulitpleValueFilterChanged(filter.propname, newfcValuesStartEnd);
                  }}
                  renderInput={(params: any) => <TextField {...params} size='small' />}
                  views={['year', 'month', 'day']}
                />
              </Grid>
            </LocalizationProvider>
          </Grid >);

      case FilterType.date:
        controlName = `filter-date-${filter.propname}`;
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns} key={`localization-date-${controlName}`}>
            <ValidatedMobileDatePicker
              key={`localization-datepicker-${controlName}`}
              inputFormat="yyyy/MM/dd"
              mask="____/__/__"
              label="Select Date"
              value={getDatepickerValue(controlName)}
              onChange={(newValue: any) => {
                let fcValues = { ...filterControlValues };
                fcValues[controlName] = newValue;
                setFilterControlValues(fcValues);
                mulitpleValueFilterChanged(filter.propname, newValue);
              }}
              renderInput={(params: any) => <TextField {...params} size='small' />}
              views={['year', 'month', 'day']}
            />
          </LocalizationProvider>);

      case FilterType.text:
          controlName = `filter-text-${filter.propname}`;
          return (
            <TextField
              id={controlName}
              key={controlName}
              defaultValue={filterControlValues[controlName]}
              size='small'
              fullWidth
              onChange={(event) => {
                let fcValues = filterControlValues;
                fcValues[controlName] = event.target.value;
                setFilterControlValues(fcValues);
                textValueFilterChanged(filter.propname, event.target.value);
              }}
            />
          );

      case FilterType.ageRange:
        controlName = `filter-number-${filter.propname}`;
          return (
            <Grid container spacing={1} alignItems= 'left' alignContent={'left'}>
              <Grid item xs={6}>
                  <TextField
                  label="Minimum Age"
                  type="number"
                  defaultValue={getAgeRangeValue(controlName, 'minimum')}
                  onChange={(event: any) => {
                    let fcValues = { ...filterControlValues };
                    let fcValuesMinMax = fcValues[controlName];
                    let newfcValuesMinMax = { ...fcValuesMinMax, minimum: + event.target.value};
                    fcValues[controlName] = newfcValuesMinMax;
                    setFilterControlValues(fcValues);                  
                    mulitpleValueFilterChanged(filter.propname, newfcValuesMinMax);
                  }}
                  size="small"
                  inputProps={{ min: 0, max: getAgeRangeValue(controlName, 'maximum') || undefined }} 
                  />
              </Grid>
              <Grid item xs={6}>
                      <TextField
                      label="Maximum Age"
                      type="number"
                      defaultValue={getAgeRangeValue(controlName, 'maximum')}
                        onChange={(event: any) => {
                        let fcValues = { ...filterControlValues };
                        let fcValuesMinMax = fcValues[controlName];
                        let newfcValuesMinMax = { ...fcValuesMinMax, maximum: + event.target.value};
                        fcValues[controlName] = newfcValuesMinMax;
                        setFilterControlValues(fcValues);
                        mulitpleValueFilterChanged(filter.propname, newfcValuesMinMax);
                      }}
                      size="small"
                      inputProps={{ min: getAgeRangeValue(controlName, 'minimum') || 0 }} 
                      />
              </Grid>
            </Grid>
          );
      
      case FilterType.checkbox:
        controlName = `filter-text-${filter.propname}`;
        return (
          <Checkbox
            key={`checkbox-${controlName}`}
            checked={filterControlValues[controlName] || false}
            onChange={(event) => {
              let fcValues = { ...filterControlValues };
              fcValues[controlName] = event.target.checked;
              setFilterControlValues(fcValues);
              checkboxValueFilterChanged(filter.propname, event.target.checked);
            }}
          />
        );
    
      case FilterType.multiselect:
      default:
        controlName = `filter-multiselect-${filter.propname}`;
        return (
          <Autocomplete
            id={controlName}
            key={controlName}
            multiple
            size='small'
            options={filter.values ?? []}
            defaultValue={filterControlValues[controlName] ?? []}
            getOptionLabel={(option) => option.key}
            onChange={(event, newValues) => {
              let fcValues = filterControlValues;
              fcValues[controlName] = newValues;
              setFilterControlValues(fcValues);
              mulitpleValueFilterChanged(filter.propname, newValues.map(o => o.val));
            }}
            renderInput={(params) => (
              <TextField {...params} />
            )}
          />
        )
    }
  }

const applyFilters = (event: any) => {
    event.preventDefault();
    let isValid = true;
    
    filters.map(f => {
      let name = f.propname;
      if (filterProps[name] === undefined) {
        if (f.type == FilterType.text){
          filterProps[name] = null;
        }
        else {
          filterProps[name] = [];
        }
      }

      if (f.type == FilterType.dateRange && filterProps[name] !== undefined) {
        const startDate = filterProps[name].start;
        const endDate = filterProps[name].end;

        if (startDate !== null && endDate !== null) {
          if (startDate > endDate) {
            toast.error("Start date cannot be after end date.");
            isValid = false;
            return;
          }
          if (endDate < startDate) {
            toast.error("End date cannot be before start date.");
            isValid = false;
            return;
          }
        }
      }

      if (f.type == FilterType.ageRange && filterProps[name] !== undefined) {
        if (filterProps[name].maximum === 0) {
          filterProps[name].maximum = null;
        }
        
        if (filterProps[name].minimum === 0) {
          filterProps[name].minimum = null;
         }
      
        if (filterProps[name].maximum !== null && filterProps[name].minimum > filterProps[name].maximum) {
         toast.error("Minimum age cannot be greater than maximum age.");
         isValid = false;
         return;
        }
      }
    });

    if (isValid) {
      filtersChanged(filterProps);
      setAnchorElFilter(null);
    }
  }

  const defaultFilterProps = () => {
    let defaultFilter: any = {};
    filters.map(f => {
      let name = f.propname;
      if (f.type == FilterType.text){
        defaultFilter[name] = null;
      }
      else {
        defaultFilter[name] = [];
      }
    });
    return defaultFilter;
  }

  const clearFilters = () => {
    setFilterControlValues({});//clear filter controls' state    
    let cleanProps = defaultFilterProps();
    setFilterProps(cleanProps);//clear filter result object
    filtersChanged(cleanProps);//trigger search
    let counter = redraw;
    setRedraw(counter++);//redraw
    setAnchorElFilter(null);
  }

  const mulitpleValueFilterChanged = (propName: string, newValues: Array<any>) => {
    let newFilters = filterProps;
    newFilters[propName] = newValues;
    setFilterProps(newFilters);
  }

  const singleValueFilterChanged = (propName: string, newValues: any) => {
    let newFilters = filterProps;
    newFilters[propName] = newValues?.val;
    setFilterProps(newFilters);
  }

  const textValueFilterChanged = (propName: string, newValue: string) => {
    let newFilters = filterProps;
    newFilters[propName] = newValue;
    setFilterProps(newFilters);
  }

  const checkboxValueFilterChanged = (propName: string, isChecked: boolean) => {
    let newFilters = filterProps;
    newFilters[propName] = isChecked;
    setFilterProps(newFilters);
  };

  // Handler for the input to set the pagination index
  const handleInputPagination = useAsyncDebounce((event: React.ChangeEvent<unknown>, value: number) => {
    if (value < 0) {
      pageNumberChanged(0);
    }
    else if (value > pageCount) {
      pageNumberChanged(pageCount);
    }
    else {
      pageNumberChanged(Number(value));
    }
  }, 200);

  // Search input value state
  const [search, setSearch] = useState(globalFilter);

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value) => {
    //setGlobalFilter(value || undefined);
    pageNumberChanged(1);
    searchValueChanged(value);
  }, 600);


  // A function that sets the sorted value for the table
  const setSortedValue: any = (column: HeaderGroup<any>) => {
    let sortedValue;

    if (column.disableSortBy) {
      sortedValue = false;
    }
    else if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? 'desc' : 'asce';
    } else if (isSorted) {
      sortedValue = 'none';
    } else {
      sortedValue = false;
    }

    return sortedValue;

  };

  // Setting the entries starting point
  const entriesStart = pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;

  // Setting the entries ending point
  let entriesEnd;

  if (pageIndex === 0) {
    if (filteredItemsCount && filteredItemsCount < pageSize) {
      entriesEnd = filteredItemsCount;
    } else {
      entriesEnd = pageSize;
    }
  } else if (pageIndex === pageOptions.length - 1) {
    //entriesEnd = rows.length;
    entriesEnd = filteredItemsCount;
  } else {
    entriesEnd = pageSize * (pageIndex + 1);
  }



  // DT FILTER 
  const [anchorElFilter, setAnchorElFilter] = React.useState<null | HTMLElement>(null);
  const filterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElFilter(anchorElFilter ? null : event.currentTarget);
  };
  const openFilter = Boolean(anchorElFilter);
  const id = openFilter ? 'simple-popper' : undefined;
  ///////////////

  const getRowBackgroundColour = (row: Row<any>) => {
    const active = row.original.active;
    if (row.original.Style?.background) {
      return row.original.Style.background;
    }
    else if (active === false) {
      return filterGrey;
    }
    else {
      return 'white !important';
    }
  }

  const getMissingDataCellBackGroundColor = (cell: any) => {
    var highlightCell = cell.column.highlightCell;
    if (!isNullOrUndefined(highlightCell)) {

      if (typeof cell.value === 'string') {
        if (cell.column?.highlightCell !== null && (isNullOrEmpty(cell.value) || cell.value === undefined)) {
          return cell.column.highlightCell;
        }
      }
      if (typeof cell.value === 'object') {
        if (cell.column?.highlightCell !== null && (isNullOrEmpty(cell.value?.key) || cell.value?.key === undefined)) {
          return cell.column.highlightCell;
        }
      }
      else {
        return 'white !important';
      }
    }
  }

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <MDBox display="flex" >
          {bulkActions.map(function (bulkAction, i) {
            return (
              <MDButton
                key={`bulk-action-${i}`}
                variant={isButtonOutlined ? "outlined" : "contained"}
                color="primary"
                sx={{ mr: 2 }}
                onClick={() => bulkAction.action()}
              >
                {bulkAction.label}
              </MDButton>
            )
          })}
        </MDBox>
      </MDBox>
      <Card>
        <MDBox mb={showBottomMargin ? 5 : 0}>
          {showEntriesPerPage && (entriesPerPage || canSearch) ? (
            <MDBox display='flex' justifyContent='space-between' alignItems='center' p={3} sx={{ '& .MuiOutlinedInput-notchedOutline legend': { width: 0 } }}>

              {/* page size */}
              {entriesPerPage && (
                <MDBox display='flex' alignItems='center'>
                  <Autocomplete
                    disableClearable
                    value={pageSize.toString()}
                    options={entries}
                    onChange={(event, newValue) => {
                      setEntriesPerPage(parseInt(newValue, 10));
                      entriesPerPageChanged(parseInt(newValue, 10));
                    }}
                    size='small'
                    sx={{ width: '5rem' }}
                    renderInput={(params) => <MDInput {...params} />}
                  />
                  <MDTypography variant='caption' color='secondary'>
                    &nbsp;&nbsp;entries per page
                  </MDTypography>
                </MDBox>
              )}
              {/* search input */}


              {canSearch && (
                <MDBox width='12rem' ml='auto'>
                  <MDInput
                    InputProps={{

                      endAdornment:
                        showSearchBoxInfoIcon ? (
                          <Tooltip
                            title={searchBoxInfoIconTooltipText}
                            arrow
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: tooltipColour,
                                  '& .MuiTooltip-arrow': {
                                    color: tooltipColour
                                  },
                                },
                              },
                            }}
                            placement="right"
                          >
                            <IconButton
                              size="small"
                              edge="end"
                              color="inherit"
                              aria-label="info"
                            >
                              <InfoOutlinedIcon style={{ color: formControlColour }} />
                            </IconButton>
                          </Tooltip>) : null
                    }
                    }
                    placeholder={searchBoxText}
                    value={search}
                    size='small'
                    fullWidth
                    onChange={({ currentTarget }: any) => {
                      setSearch(currentTarget.value);
                      onSearchChange(currentTarget.value);
                    }}
                  />
                </MDBox>
              )}

              {/* filters */}

              {filters.length > 0 && <MDBox>
                <IconButton aria-describedby={id}
                  color="primary"
                  disabled={!filters}
                  //component="span" 
                  onClick={filterClick} >
                  <FilterAltIcon />
                </IconButton>
                <Popper id={id}
                  open={openFilter}
                  anchorEl={anchorElFilter}
                  placement="bottom-end"
                  sx={{ zIndex: theme.zIndex.modal }}
                >
                  <Box sx={{
                    bgcolor: 'background.paper',
                    boxShadow: 2,
                    borderRadius: 3,
                    p: 2,
                    border: '1px solid rgba(0, 0, 0, 0.25)',
                    maxWidth: 530,
                    minWidth: 400
                  }}>
                    {/* The content of the Popper. */}
                    {
                      renderFilters()
                    }
                  </Box>
                </Popper>
              </MDBox>}

            </MDBox>
          ) : null}
          <TableContainer sx={{ boxShadow: 'none', borderRadius: 0, overflow: 'auto' }} >
            {isLoading ?
              <SkeletonLoading tableLoading={true} />
              :
              <MDBox>
                <div style={{ overflowX: "auto" }} ref={tableContainerRef} className={overflowActive && lastColumnIsActions ? "fixed-Action-column" : "fixed-width-column"}>
                  <Table {...getTableProps()} >
                    <MDBox component='thead'>
                      {headerGroups.map((headerGroup, rowIndex) => (
                        <TableRow {...headerGroup.getHeaderGroupProps()} sx={{ backgroundColor: tableHeaderColour }} key={`table-header-row-${rowIndex}`}>
                          {headerGroup.headers.map((column, colIndex) => (
                            <DataTableHeadCell
                              {...column.getHeaderProps(isSorted as any && column.getSortByToggleProps())}
                              width={column.width ? column.width : 'auto'}
                              sorted={setSortedValue(column)}
                              key={`table-header--column-${colIndex}`}
                              //@ts-ignore
                              costAlign={column.rightAlign ? true : false}
                              //@ts-ignore
                              showInfoIcon={column.infoIconEnabled}
                              //@ts-ignore
                              infoTooltip={column.infoTooltip}
                              //@ts-ignore
                              onInfoClick={column.onInfoClick}
                              //@ts-ignore
                              noWrap={column.noWrap}
                            >
                              {column.render('Header')}
                            </DataTableHeadCell>
                          ))}
                        </TableRow>
                      ))}
                    </MDBox>

                    <TableBody {...getTableBodyProps()}>
                      {page.map((row: Row<any>, key: number) => {
                        prepareRow(row);
                        return (
                          <TableRow {...row.getRowProps()} key={`table-body-row-${key}`} sx={{ backgroundColor: getRowBackgroundColour(row) }} >
                            {row.cells.map((cell) => (
                              <DataTableBodyCell
                                noBorder={noEndBorder && rows.length - 1 === key}
                                {...cell.getCellProps()}
                                //@ts-ignore
                                align={cell.column.rightAlign ? "right" : "left"}
                                //@ts-ignore
                                highlightCell={getMissingDataCellBackGroundColor(cell)}
                                //@ts-ignore
                                noWrap={cell.column.noWrap}
                              >
                                {cell.render('Cell')}
                              </DataTableBodyCell>
                            ))}
                          </TableRow>
                        )
                      })}
                      {!isLoading && page.length === 0 && noDataMessage && (
                        <TableRow>
                          <TableCell colSpan={headerGroups[0]?.headers.length ?? 1} align="center" sx={{ border: 0, py: 2 }}>
                            <MDTypography variant="caption">{noDataMessage}</MDTypography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </MDBox>
            }
          </TableContainer>
          <MDBox
            display='flex'
            flexDirection={{ xs: 'column', sm: 'row' }}
            justifyContent='space-between'
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            p={(!showTotalEntries && pageOptions.length <= 1) || (noDataMessage && page.length === 0) ? 0 : 3}
          >
            {showTotalEntries && !(noDataMessage && page.length === 0) && (
              <MDBox mb={{ xs: 3, sm: 0 }}>
                <MDTypography variant='button' color='secondary' fontWeight='regular'>
                  Showing {entriesStart} to {entriesEnd} of {filteredItemsCount} entries{showFilteredCount && ` - filtered from ${totalItemsCount} entries`}
                </MDTypography>
              </MDBox>
            )}
            {pageOptions.length > 1 && (
              <Pagination
                count={pageCount}
                showFirstButton
                showLastButton
                onChange={handleInputPagination}
                page={pageNumber + 1}
                variant="outlined"
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: dark.main,
                    border: '1px solid rgb(222, 226, 230)',
                    '&.Mui-selected': {
                      background: 'linear-gradient(195deg, rgb(119, 156, 170), rgb(9, 115, 132))',
                      color: '#fff',
                      border: 0,
                      boxShadow: 'rgba(9, 115, 132, 0.15) 0rem 0.1875rem 0.1875rem 0rem, rgba(9, 115, 132, 0.2) 0rem 0.1875rem 0.0625rem -0.125rem, rgba(9, 115, 132, 0.15) 0rem 0.0625rem 0.3125rem 0rem'
                    },
                    '&.MuiPaginationItem-ellipsis': {
                      border: 0
                    }
                  }
                }}
              />
            )}
          </MDBox>
        </MDBox>
      </Card>
    </MDBox>
  );
}

